import { useQuery } from "@apollo/client/react"
import { lazy, Suspense, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { dashboardPath, puzzleLabelPath, userPublicProfilePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import wordmarkUrl from "~/images/wp-mascot-wordmark.png"
import { SiteHeader } from "~/layouts/site-header"
import { Badge } from "~/ui/badge"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { JourneyPhotoLightbox } from "./journey-photo-lightbox"

const PuzzleJourneyMap = lazy(() =>
  import("~/maps/puzzle-journey-map").then((m) => ({ default: m.PuzzleJourneyMap }))
)

const puzzleQuery = gql(/* GraphQL */ `
  query PuzzleTracking($wpId: String!) {
    puzzle(wpId: $wpId) {
      id
      wpId
      title
      brand
      pieceCount
      homeCity
      homeState
      registeredOn
      photoUrl
      photoThumbUrl
      journeyLogCount
      viewerIsOwner
      journeyLogs {
        nodes {
          id
          completedOn
          city
          state
          comment
          authorDisplayName
          author {
            id
            firstName
          }
          passedOn
          nextLocation
          latitude
          longitude
          displayOnMap
          photoUrl
          photoThumbUrl
        }
      }
    }
  }
`)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

export const PuzzleTrackingScreen = () => {
  const { wpId } = useParams<{ wpId: string }>()
  const { viewer } = useViewerMaybe()
  const { data, loading, error } = useQuery(puzzleQuery, {
    variables: { wpId: wpId ?? "" },
    skip: !wpId,
  })
  const [openLogId, setOpenLogId] = useState<string | null>(null)

  const puzzle = data?.puzzle
  useDocumentTitle(puzzle ? `${puzzle.title} (${puzzle.wpId})` : wpId ? `Puzzle ${wpId}` : null)

  if (loading) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />
  if (!puzzle) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-2xl font-semibold">Puzzle not found</h1>
        <p className="mt-2 text-gray-600">
          We couldn&apos;t find a puzzle with id <span className="font-mono">{wpId}</span>.
        </p>
        <Link to="/" className="mt-6 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  const logs = puzzle.journeyLogs.nodes

  return (
    <>
      <SiteHeader />
      <header className="bg-mascot-blue-pale text-stone-900">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-5 sm:gap-6 sm:py-6">
          <Link to="/" className="block shrink-0">
            <img
              src={wordmarkUrl}
              alt="Wandering Puzzles"
              className="h-16 w-16 rounded-full bg-white object-cover sm:h-20 sm:w-20"
            />
          </Link>
          <div>
            <h2 className="font-display text-xl font-semibold leading-tight sm:text-2xl">
              This is a Wandering Puzzle.
            </h2>
            <p className="mt-1 text-sm leading-snug text-stone-700 sm:text-base">
              See where it&apos;s been, who&apos;s finished it before, and share your story as part
              of its journey!
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        {viewer ? (
          <Link
            to={dashboardPath({})}
            className="mb-6 inline-block text-sm text-stone-500 hover:text-stone-800"
          >
            ← Back to dashboard
          </Link>
        ) : null}
        <section className="grid items-start gap-6 sm:grid-cols-[1fr_2fr]">
          <div>
            <div className="overflow-hidden rounded-lg border bg-stone-100">
              {puzzle.photoUrl ? (
                <img src={puzzle.photoUrl} alt={puzzle.title} className="w-full object-contain" />
              ) : (
                <div className="aspect-square w-full" />
              )}
            </div>
            {puzzle.viewerIsOwner ? (
              <Link
                to={puzzleLabelPath({ wpId: puzzle.wpId })}
                className="mt-3 inline-block text-sm text-mountain hover:underline"
              >
                Download or print sticker →
              </Link>
            ) : null}
          </div>
          <div>
            <Badge variant="secondary" className="font-mono">
              {puzzle.wpId}
            </Badge>
            <h1 className="mt-2 text-3xl font-semibold leading-tight">{puzzle.title}</h1>
            <p className="mt-1 text-gray-600">{puzzle.brand}</p>
            <dl className="mt-4 grid grid-cols-2 gap-y-1 text-sm">
              <dt className="text-gray-500">Pieces</dt>
              <dd>{puzzle.pieceCount.toLocaleString()}</dd>
              {puzzle.homeCity || puzzle.homeState ? (
                <>
                  <dt className="text-gray-500">Started in</dt>
                  <dd>{[puzzle.homeCity, puzzle.homeState].filter(Boolean).join(", ")}</dd>
                </>
              ) : null}
              <dt className="text-gray-500">Registered</dt>
              <dd>{formatDate(puzzle.registeredOn)}</dd>
              <dt className="text-gray-500">Journey logs</dt>
              <dd>{puzzle.journeyLogCount}</dd>
            </dl>
          </div>
        </section>

        <section className="mt-10 rounded-lg border bg-gray-50 p-6 text-center">
          <h2 className="text-lg font-medium">Have you completed this puzzle?</h2>
          <p className="mt-1 text-sm text-gray-600">
            Add your stop to its journey. It only takes a minute.
          </p>
          <Link
            to={`/p/${puzzle.wpId}/log`}
            className="mt-4 inline-block rounded-md bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Log your journey
          </Link>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-semibold">Journey</h2>

          {logs.some((l) => l.displayOnMap && l.latitude != null && l.longitude != null) ? (
            <Suspense
              fallback={<div className="mt-4 h-72 animate-pulse rounded-lg bg-stone-100 sm:h-96" />}
            >
              <PuzzleJourneyMap logs={logs} className="mt-4 h-72 sm:h-96" />
            </Suspense>
          ) : null}

          {logs.length === 0 ? (
            <Card className="mt-4">
              <CardContent className="py-8 text-center text-gray-500">
                No one has logged this puzzle yet. Be the first.
              </CardContent>
            </Card>
          ) : (
            <ol className="mt-6 space-y-4">
              {logs.map((log) => (
                <li key={log.id}>
                  <Card>
                    <CardContent className="py-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div>
                          {log.author ? (
                            <Link
                              to={userPublicProfilePath({ userId: log.author.id })}
                              className="font-medium hover:underline"
                            >
                              {log.authorDisplayName}
                            </Link>
                          ) : (
                            <span className="font-medium">{log.authorDisplayName}</span>
                          )}
                          {log.city || log.state ? (
                            <span className="text-gray-500">
                              {" · "}
                              {[log.city, log.state].filter(Boolean).join(", ")}
                            </span>
                          ) : null}
                        </div>
                        <time className="text-sm text-gray-500" dateTime={log.completedOn}>
                          {formatDate(log.completedOn)}
                        </time>
                      </div>
                      {log.comment ? <p className="mt-2 text-gray-800">{log.comment}</p> : null}
                      {log.passedOn ? (
                        <p className="mt-2 text-sm text-gray-500">
                          Passed on{log.nextLocation ? ` to ${log.nextLocation}` : ""}.
                        </p>
                      ) : null}
                      {log.photoUrl ? (
                        <button
                          type="button"
                          onClick={() => setOpenLogId(log.id)}
                          aria-label={`Open photo from ${log.authorDisplayName}`}
                          className="mt-3 block overflow-hidden rounded-md border focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                        >
                          <img
                            src={log.photoThumbUrl ?? log.photoUrl}
                            alt=""
                            className="max-h-72 transition hover:opacity-90"
                          />
                        </button>
                      ) : null}
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ol>
          )}
        </section>

        <footer className="mt-12 text-center text-sm text-gray-500">
          <Link to={`/contact?puzzleId=${puzzle.wpId}`} className="underline">
            Report an issue with this puzzle
          </Link>
        </footer>
      </main>

      <JourneyPhotoLightbox
        logs={logs}
        openLogId={openLogId}
        onOpenChange={(open) => {
          if (!open) setOpenLogId(null)
        }}
        onNavigate={setOpenLogId}
      />
    </>
  )
}
